import {
  OAuthClientCreationRequest,
  OAuthClientList,
  OAuthClientRepresentation,
  OrganizationRepresentation,
  UpdateClientRequest,
} from "../infrastructure/api/api";
import { getHalJSON, post, remove } from "../infrastructure/api/digipost-fetch";
import { cached, invalidate } from "../infrastructure/api/cache";
import {
  findUriIfExists,
  MissingLinkError,
} from "../infrastructure/entrypoint/link";

export interface ClientsApi {
  listClients: () => Promise<OAuthClientRepresentation[]>;
  createClient: (
    request: OAuthClientCreationRequest,
  ) => Promise<OAuthClientRepresentation>;
  updateClient: (
    clientId: string,
    request: UpdateClientRequest,
  ) => Promise<void>;
  deleteClient: (clientId: string) => Promise<void>;
}

export function clientsApi(
  organization: OrganizationRepresentation,
): ClientsApi {
  const clientsUrl = findUriIfExists("clients", organization._links);

  const listClients = (): Promise<OAuthClientRepresentation[]> =>
    clientsUrl
      ? cached(
          clientsUrl,
          async () => (await getHalJSON<OAuthClientList>(clientsUrl)).clients,
        )
      : Promise.resolve([]);

  const invalidateClients = () => {
    if (clientsUrl) {
      invalidate(clientsUrl);
    }
  };

  const requireClient = async (clientId: string) => {
    const client = (await listClients()).find(
      (c) => c.client.clientId === clientId,
    );
    if (!client) {
      throw new Error(`Client ${clientId} not found`);
    }

    return client;
  };

  return {
    listClients,

    createClient: async (request) => {
      const createUrl = findUriIfExists("create_client", organization._links);
      if (!createUrl) {
        throw new MissingLinkError("create_client");
      }

      const response = await post(createUrl, request);
      invalidateClients();

      return response.json();
    },

    updateClient: async (clientId, request) => {
      const client = await requireClient(clientId);
      const updateUrl = findUriIfExists("update", client._links);
      if (!updateUrl) {
        throw new MissingLinkError("update");
      }

      await post(updateUrl, request);
      invalidateClients();
    },

    deleteClient: async (clientId) => {
      const client = await requireClient(clientId);
      const deleteUrl = findUriIfExists("delete", client._links);
      if (!deleteUrl) {
        throw new MissingLinkError("delete");
      }

      await remove(deleteUrl, "");
      invalidateClients();
    },
  };
}
